import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    :host {
      display: inline-block;
      vertical-align: middle;
    }
    :host([fluid]) { display: block; width: 100%; }

    /* ── Wrapper ─────────────────────────────────────────────────────── */
    .uwc-textarea {
      --_bg:        var(--uwc-textarea-bg,                ${t.surface});
      --_border:    var(--uwc-textarea-border,            1px solid ${t.borderControl});
      --_radius:    var(--uwc-textarea-radius,            ${t.radiusMd});
      --_color:     var(--uwc-textarea-color,             ${t.text});
      --_ph-color:  var(--uwc-textarea-placeholder-color, ${t.textMuted});
      --_font-size: var(--uwc-textarea-font-size,         ${t.fontSizeMd});
      --_px:        var(--uwc-textarea-padding-x,         0.75rem);
      --_py:        var(--uwc-textarea-padding-y,         0.5rem);

      display:       block;
      width:         100%;
      background:    var(--_bg);
      border:        var(--_border);
      border-radius: var(--_radius);
      transition:    border-color ${t.durationBase}, box-shadow ${t.durationBase};
    }

    .uwc-textarea:focus-within {
      border-color: var(--uwc-color-primary, ${t.primary});
      box-shadow:   0 0 0 3px color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 20%, transparent);
    }

    /* ── Variants ────────────────────────────────────────────────────── */
    .uwc-textarea--filled {
      --_bg: var(--uwc-textarea-filled-bg, ${t.surfaceRaised});
      border-color: transparent;
    }
    .uwc-textarea--filled:focus-within {
      --_bg: var(--uwc-textarea-bg, ${t.surface});
      border-color: var(--uwc-color-primary, ${t.primary});
    }

    /* ── Invalid ─────────────────────────────────────────────────────── */
    .uwc-textarea--invalid {
      border-color: var(--uwc-color-danger, ${t.danger}) !important;
    }
    .uwc-textarea--invalid:focus-within {
      box-shadow: 0 0 0 3px color-mix(in oklab, var(--uwc-color-danger, ${t.danger}) 20%, transparent);
    }

    /* ── Sizes ───────────────────────────────────────────────────────── */
    .uwc-textarea--small { --_font-size: ${t.fontSizeXs}; --_px: 0.5rem;  --_py: 0.3125rem; }
    .uwc-textarea--large { --_font-size: ${t.fontSizeLg}; --_px: 1rem;    --_py: 0.6875rem; }

    /* ── Disabled ────────────────────────────────────────────────────── */
    .uwc-textarea--disabled { pointer-events: none; opacity: 0.6; }

    /* ── Native textarea ─────────────────────────────────────────────── */
    textarea {
      display:     block;
      width:       100%;
      min-width:   0;
      padding:     var(--_py) var(--_px);
      font-family: var(--uwc-font-family, inherit);
      font-size:   var(--_font-size);
      line-height: 1.5;
      color:       var(--_color);
      background:  transparent;
      border:      none;
      outline:     none;
      box-sizing:  border-box;
      resize:      var(--uwc-textarea-resize, vertical);
    }

    /* Resize variants */
    :host([resize="none"])       textarea { resize: none; }
    :host([resize="both"])       textarea { resize: both; }
    :host([resize="horizontal"]) textarea { resize: horizontal; }
    :host([resize="vertical"])   textarea { resize: vertical; }
    :host([auto-resize])         textarea { resize: none; overflow: hidden; }

    textarea::placeholder { color: var(--_ph-color); }
    textarea:disabled      { cursor: not-allowed; }

    /* ── Character count bar ─────────────────────────────────────────── */
    .uwc-textarea__footer {
      display:         flex;
      justify-content: flex-end;
      padding:         0.25rem var(--_px) 0.3125rem;
      border-top:      1px solid var(--uwc-textarea-footer-border, ${t.borderSubtle});
    }

    .uwc-textarea__count {
      font-size:  ${t.fontSizeXs};
      color:      var(--uwc-textarea-count-color, ${t.textMuted});
      font-variant-numeric: tabular-nums;
    }

    .uwc-textarea__count--warn {
      color: var(--uwc-color-danger, ${t.danger});
    }

    /* ── Float label ─────────────────────────────────────────────────── */
    .uwc-textarea--float { position: relative; }

    .uwc-textarea__label {
      position:       absolute;
      left:           calc(var(--_px) + 0.125rem);
      line-height:    1;
      white-space:    nowrap;
      pointer-events: none;
      padding-inline: 0.25rem;
      transition:
        top       ${t.durationBase} ${t.easingStandard},
        font-size ${t.durationBase} ${t.easingStandard},
        color     ${t.durationBase} ${t.easingStandard},
        transform ${t.durationBase} ${t.easingStandard};
    }

    /* Over (default) */
    .uwc-textarea--float-over {
      overflow:   visible;
      margin-top: 1.5rem;
    }
    .uwc-textarea--float-over .uwc-textarea__label {
      top:        0.6rem;
      font-size:  var(--_font-size);
      color:      var(--_ph-color);
      background: transparent;
      transform:  none;
    }
    .uwc-textarea--float-over:focus-within .uwc-textarea__label,
    .uwc-textarea--float-over textarea:not(:placeholder-shown) ~ .uwc-textarea__label {
      top:       0;
      transform: translateY(calc(-100% - 0.375rem));
      font-size: calc(var(--_font-size) * 0.85);
      color:     var(--uwc-textarea-label-active-color, var(--uwc-color-primary, ${t.primary}));
    }

    /* On */
    .uwc-textarea--float-on {
      overflow:   visible;
      margin-top: 0.75rem;
    }
    .uwc-textarea--float-on .uwc-textarea__label {
      top:        0.6rem;
      font-size:  var(--_font-size);
      color:      var(--_ph-color);
      background: var(--_bg);
      transform:  none;
    }
    .uwc-textarea--float-on:focus-within .uwc-textarea__label,
    .uwc-textarea--float-on textarea:not(:placeholder-shown) ~ .uwc-textarea__label {
      top:       0;
      transform: translateY(-50%);
      font-size: calc(var(--_font-size) * 0.8);
      color:     var(--uwc-textarea-label-active-color, var(--uwc-color-primary, ${t.primary}));
    }
    .uwc-textarea--float-on.uwc-textarea--filled .uwc-textarea__label {
      background: var(--uwc-textarea-filled-bg, ${t.surfaceRaised});
    }

    /* In */
    .uwc-textarea--float-in textarea {
      padding-top: 1.5rem;
    }
    .uwc-textarea--float-in .uwc-textarea__label {
      top:            0.6rem;
      font-size:      var(--_font-size);
      color:          var(--_ph-color);
      background:     transparent;
      left:           var(--_px);
      padding-inline: 0;
      transform:      none;
    }
    .uwc-textarea--float-in:focus-within .uwc-textarea__label,
    .uwc-textarea--float-in textarea:not(:placeholder-shown) ~ .uwc-textarea__label {
      top:            0.3125rem;
      transform:      none;
      font-size:      calc(var(--_font-size) * 0.75);
      color:          var(--uwc-textarea-label-active-color, var(--uwc-color-primary, ${t.primary}));
      left:           var(--_px);
      padding-inline: 0;
    }

    /* Invalid label */
    .uwc-textarea--invalid .uwc-textarea__label,
    .uwc-textarea--invalid:focus-within .uwc-textarea__label,
    .uwc-textarea--invalid textarea:not(:placeholder-shown) ~ .uwc-textarea__label {
      color: var(--uwc-color-danger, ${t.danger});
    }
  `,
];
